import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, CardActions, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  CampaignOutlined,
  AddCircleOutlined,
  ArchiveOutlined,
  ArrowForwardRounded,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import OrganizationHeader from "../organization/components/OrganizationHeader";

export default function Communication() {
  const { t } = useTranslation();
  const theme = useTheme();

  const communicationOptions = [
    {
      title: t("communication.newAnnouncement"),
      description: t("communication.newAnnouncementDesc", {
        defaultValue:
          "Create and publish new announcements to inform your organization about important updates and news.",
      }),
      icon: <AddCircleOutlined sx={{ fontSize: 36 }} />,
      path: "/communications/announcement",
      color: theme.palette.primary.main,
      aria: t("communication.newAnnouncementAria"),
    },
    {
      title: t("communication.newsArchive"),
      description: t("communication.newsArchiveDesc", {
        defaultValue:
          "Browse, manage and delete existing announcements from your organization's archive.",
      }),
      icon: <ArchiveOutlined sx={{ fontSize: 36 }} />,
      path: "/communications/archive",
      color: theme.palette.secondary.main,
      aria: t("communication.newsArchiveAria"),
    },
  ];

  return (
    <>
      <OrganizationHeader
        icon={CampaignOutlined}
        title={t("communication.headerTitle", {
          defaultValue: "Communication Center",
        })}
        subtitle={t("communication.headerSubtitle", {
          defaultValue: "Manage organizational announcements and communication",
        })}
      />

      {/* Communication Options Cards */}
      <Box sx={{ px: 3 }}>
        <Grid container spacing={3}>
          {communicationOptions.map((option, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: theme.shadows[8],
                    borderColor: option.color,
                    "& .option-icon": {
                      transform: "scale(1.1)",
                      color: option.color,
                    },
                    "& .option-arrow": {
                      transform: "translateX(8px)",
                    },
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      className="option-icon"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 70,
                        height: 70,
                        borderRadius: 3,
                        bgcolor: alpha(option.color, 0.1),
                        border: "2px solid",
                        borderColor: alpha(option.color, 0.2),
                        color: alpha(option.color, 0.7),
                        transition: "all 0.3s ease",
                        mb: 2,
                      }}
                    >
                      {option.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 1, color: "text.primary", fontSize: "1rem" }}
                    >
                      {option.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.5,
                        fontSize: "0.8125rem",
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    component={Link}
                    to={option.path}
                    variant="contained"
                    fullWidth
                    endIcon={
                      <ArrowForwardRounded
                        className="option-arrow"
                        sx={{
                          fontSize: 18,
                          transition: "transform 0.3s ease",
                        }}
                      />
                    }
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      background: `linear-gradient(135deg, ${option.color} 0%, ${alpha(option.color, 0.8)} 100%)`,
                      "&:hover": {
                        boxShadow: theme.shadows[6],
                        background: `linear-gradient(135deg, ${alpha(option.color, 0.9)} 0%, ${alpha(option.color, 0.7)} 100%)`,
                      },
                      transition: "all 0.3s ease",
                    }}
                    aria-label={option.aria}
                  >
                    {t("communication.manage", { defaultValue: "Manage" })}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}
